/**
 * Casting, without a button that does nothing.
 *
 * ## Why this is availability-driven rather than a permanent control
 *
 * The Remote Playback API can tell you whether a device is actually reachable,
 * and the whole design here hangs off that. A cast button that is always
 * visible is a promise the page cannot keep: on a laptop with no Chromecast on
 * the network, tapping it opens a picker that finds nothing, and the listener
 * concludes the feature is broken rather than that their network has no
 * receiver.
 *
 * So {@link watchAvailability} is the entry point, and the UI is expected to
 * show its control ONLY while the callback has last reported `true`. Nothing
 * here renders anything; the decision is handed to the caller because the two
 * apps draw it differently.
 *
 * ## What this does not do
 *
 * It does not implement the Google Cast SDK. Cast-specific features -- queues
 * on the receiver, custom receiver apps, media metadata beyond what the
 * element carries -- are out of reach of this API and would need the SDK and,
 * on mobile, a platform channel. Remote Playback covers the case that matters
 * for a podcast: send THIS element to THAT device and keep the transport
 * controls.
 *
 * ## Browser reality, measured
 *
 * Chromium implements `HTMLMediaElement.remote` for both audio and video.
 * Safari does not, and exposes AirPlay through
 * `webkitShowPlaybackTargetPicker` with `webkitplaybacktargetavailabilitychanged`
 * for availability -- so both paths are handled, and a browser with neither is
 * reported unsupported rather than left to throw at the first tap.
 */

/** What a caller needs to know before drawing anything. */
export interface RemoteSupport {
  /** Whether ANY casting path exists in this browser. */
  supported: boolean;
  /** Which one, for logs and for tests that want to be specific. */
  kind: 'remote-playback' | 'airplay' | 'none';
}

/** Stops an availability watch. Idempotent. */
export type StopWatching = () => void;

/**
 * What this module touches, structurally.
 *
 * Deliberately NOT `extends HTMLMediaElement`: the DOM lib declares `remote`
 * as a required `RemotePlayback`, so narrowing it to an optional of our own
 * shape is a type error rather than a refinement. Browsers that lack it are
 * the entire case being handled here, so the optional is the honest type and
 * the cast goes through `unknown`.
 */
interface RemoteCapableElement {
  remote?: {
    watchAvailability(cb: (available: boolean) => void): Promise<number>;
    cancelWatchAvailability(id?: number): Promise<void>;
    prompt(): Promise<void>;
  };
  webkitShowPlaybackTargetPicker?: () => void;
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
}

const asRemote = (el: HTMLMediaElement): RemoteCapableElement => el as unknown as RemoteCapableElement;

/**
 * Which casting path this browser offers, if any.
 *
 * Takes the element rather than reading `window`, because support is a
 * property of the ELEMENT: Safari's AirPlay hooks live on the media element
 * and Chromium's `remote` is an own property of it.
 */
export function supportFor(element: HTMLMediaElement): RemoteSupport {
  const el = asRemote(element);
  if (el.remote && typeof el.remote.watchAvailability === 'function') {
    return { supported: true, kind: 'remote-playback' };
  }
  if (typeof el.webkitShowPlaybackTargetPicker === 'function') {
    return { supported: true, kind: 'airplay' };
  }
  return { supported: false, kind: 'none' };
}

/**
 * Calls back whenever a receiver appears or disappears.
 *
 * Fires once with the current state before any change, so a caller never has
 * to guess an initial value. Returns a function that stops the watch; calling
 * it twice is safe, because a widget's dispose can race its own rebuild.
 *
 * A browser with no support calls back `false` once and returns a no-op, which
 * means the caller needs no branch of its own: "watch, and show the control
 * while it says true" is correct everywhere.
 */
export function watchAvailability(element: HTMLMediaElement, onChange: (available: boolean) => void): StopWatching {
  const el = asRemote(element);
  const support = supportFor(element);

  if (!support.supported) {
    onChange(false);
    return () => {};
  }

  if (support.kind === 'airplay') {
    const handler = (event: Event) => {
      // Safari reports availability on the event, not on the element.
      const availability = (event as Event & { availability?: string }).availability;
      onChange(availability === 'available');
    };
    el.addEventListener('webkitplaybacktargetavailabilitychanged', handler);
    // No way to query the current value up front; Safari fires the event on
    // its own shortly after the element has a source. `false` until then is
    // the honest answer and matches what the picker would find.
    onChange(false);
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      el.removeEventListener('webkitplaybacktargetavailabilitychanged', handler);
    };
  }

  let watchId: number | null = null;
  let stopped = false;
  void el.remote!.watchAvailability(onChange).then(
    (id) => {
      // Disposed while the promise was in flight: cancel immediately rather
      // than leave a callback pointing at a widget that is gone.
      if (stopped) {
        void el.remote!.cancelWatchAvailability(id).catch(() => {});
        return;
      }
      watchId = id;
    },
    () => {
      // Some Chromium builds reject when the element has no source yet. Not
      // an error worth surfacing: it means "no device", which is what the
      // caller already assumes.
      onChange(false);
    }
  );

  return () => {
    if (stopped) return;
    stopped = true;
    if (watchId !== null) {
      void el.remote!.cancelWatchAvailability(watchId).catch(() => {});
    }
  };
}

/**
 * Opens the device picker. Must be called from a user gesture.
 *
 * Resolves `true` if a picker was shown, `false` if this browser has no way to
 * show one. It deliberately does NOT reject when the listener dismisses the
 * picker: cancelling is a normal thing to do and is not a failure the UI
 * should report.
 */
export async function promptForDevice(element: HTMLMediaElement): Promise<boolean> {
  const el = asRemote(element);
  const support = supportFor(element);
  if (!support.supported) return false;

  if (support.kind === 'airplay') {
    el.webkitShowPlaybackTargetPicker!();
    return true;
  }

  try {
    await el.remote!.prompt();
    return true;
  } catch {
    // NotAllowedError (dismissed), InvalidStateError (no device by the time
    // the picker opened). Neither is worth a message.
    return true;
  }
}
